import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/common/loader';
import PageHeader from '../../components/page-header';
import { OrganisationContext } from '../../context/organisationContext';
import useDebounce from '../../helpers/useDebounceHook';
import { getTemplateList } from '../../store/features/templatesSlice';
import { TemplateListWrapper } from './templates.styled';

/**
 * Templates list, the same shape as pages/forms: search, pagination, click through to versions.
 */
const Templates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setModal } = useContext(OrganisationContext);
  const { templatesList } = useSelector(state => state.templates);
  const [search, setSearchText] = useState('');
  const [selectedPage, setSelectedPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  const fetchTemplates = currentPage => {
    setLoading(true);
    dispatch(getTemplateList({ params: { search: debouncedSearch, page: currentPage } }))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    setSelectedPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchTemplates(selectedPage);
  }, [debouncedSearch, selectedPage]);

  const handleAddTemplate = () => {
    setModal({
      type: 'add-template',
      content: { onSuccess: () => fetchTemplates(selectedPage) },
    });
  };

  const templates = templatesList?.content || [];

  return (
    <TemplateListWrapper className="flex-column flex-1">
      <PageHeader
        names={[{ text: 'Templates', onClick: () => {} }]}
        showSearch
        showPagination
        showAddNewBtn
        addBtnPermission="FORMS_MANAGE"
        onAddClick={handleAddTemplate}
        searchText={search}
        onSearchChange={setSearchText}
        pagination={{
          selectedPage: selectedPage,
          totalPages: templatesList?.total_pages || 0,
          setSelectedPage: setSelectedPage,
        }}
      />

      <div className="flex-column flex-1 mt-6 mx-6 overflow-auto">
        <div className="list-header pxy-4">
          <label className="medium-text">Name</label>
          <label className="medium-text">Description</label>
          <label className="medium-text">Status</label>
          <label className="medium-text">Format</label>
        </div>

        {loading ? (
          <Loader />
        ) : templates.length === 0 ? (
          <label className="muted regular-text mt-10 flex justify-center">No templates yet</label>
        ) : (
          templates.map(template => (
            <div
              key={template.id}
              className="list-row pxy-4"
              onClick={() => navigate(`/templates/${template.id}/versions`)}>
              <label className="regular-text">{template.name}</label>
              <label className="muted regular-text">{template.description || '—'}</label>
              <label className={`status-pill regular-text ${template.status === 'ARCHIVED' ? 'archived' : 'active'}`}>
                {template.status === 'ARCHIVED' ? 'Archived' : 'Active'}
              </label>
              <label className="muted regular-text">
                {`${template.format || 'A4'}${template.landscape ? ' landscape' : ''}`}
              </label>
            </div>
          ))
        )}
      </div>
    </TemplateListWrapper>
  );
};

export default Templates;
